/* Common Imports */

import { styled } from "@mui/system";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import { Price } from "../../../../../redux-magic/sub-interfaces/sub-interfaces";

/* Component Imports */

import { Skeleton, Typography } from "@mui/material";

/* Icon Imports */

import { CurrencyRupee } from "@mui/icons-material";

/* Library Imports */

import PreparePriceString from "../../../../../lib/PreparePriceString";

/* Styled Components */

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	gap: "0.25rem",
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	fontWeight: 500,
	fontSize: "0.875rem",
	lineHeight: "0.875rem",
	letterSpacing: "0rem",
	margin: "0rem 0rem 0rem 0rem",
	color: "#1e4620",
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		fontSize: "0.7rem",
		lineHeight: "0.7rem",
	},
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		fontSize: "0.75rem",
		lineHeight: "0.75rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	fontWeight: 600,
	fontSize: "0.875rem",
	lineHeight: "0.875rem",
	letterSpacing: "0rem",
	margin: "0rem 0rem 0rem 0rem",
	color: "#1e4620",
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		fontSize: "0.7rem",
		lineHeight: "0.7rem",
	},
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		fontSize: "0.775rem",
		lineHeight: "0.775rem",
	},
	/* iphone se - 375 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
		fontSize: "0.825rem",
		lineHeight: "0.825rem",
	},
	/*iphone XR - 414*/
	[theme.breakpoints.up(theme.breakpoints.values.xs + 130)]: {
		fontSize: "0.875rem",
		lineHeight: "0.875rem",
	},
	/* 570  ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.xsPlus + 30)]: {
		fontSize: "0.75rem",
		lineHeight: "0.75rem",
	},
	/* 600  ~~2 cards start~~ */
	[theme.breakpoints.up("sm")]: {
		fontSize: "0.875rem",
		lineHeight: "0.875rem",
	},
	/* 642 ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 42)]: {
		fontSize: "0.775rem",
		lineHeight: "0.775rem",
	},
	/* 684 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 84)]: {
		fontSize: "0.825rem",
		lineHeight: "0.825rem",
	},
	/*ipad Mini */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		fontSize: "0.875rem",
		lineHeight: "0.875rem",
	},
	/* ipad Air*/
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		fontSize: "0.95rem",
		lineHeight: "0.95rem",
	},
	/*910*/
	[theme.breakpoints.up("sm910")]: {
		fontSize: "1rem",
		lineHeight: "1rem",
	},
	/* 1080p breakpoint */
	[theme.breakpoints.up("xl")]: {
		fontSize: "1rem",
		lineHeight: "1rem",
	},
}));

const RupeeIcon = styled(CurrencyRupee)(({ theme }) => ({
	margin: "0rem 0rem 0rem 0rem",
	fontSize: "0.875rem",
	/* iphone 5 - 320 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
		fontSize: "0.7rem",
	},
	/* galaxy S8+ - 360 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 78)]: {
		fontSize: "0.775rem",
	},
	/* iphone se - 375 */
	[theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
		fontSize: "0.825rem",
	},
	/* 570  ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.xsPlus + 30)]: {
		fontSize: "0.75rem",
	},
	/* 642 ~~3 cards start~~ */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 42)]: {
		fontSize: "0.7rem",
	},
	/* 684 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 84)]: {
		fontSize: "0.825rem",
	},
	/*ipad Mini */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		fontSize: "0.875rem",
	},
	/* ipad Air*/
	[theme.breakpoints.up(theme.breakpoints.values.sm + 219)]: {
		fontSize: "0.95rem",
	},
	/*860*/
	[theme.breakpoints.up("sm860")]: {
		fontSize: "0.95rem",
		margin: "0.1rem 0rem 0rem 0rem",
	},
	/*910*/
	[theme.breakpoints.up("sm910")]: {
		fontSize: "1rem",
		margin: "0rem 0rem 0rem 0rem",
	},
	/* 1080p breakpoint */
	[theme.breakpoints.up("xl")]: {
		fontSize: "1rem",
	},
}));

const GridCardPrice = ({ router, price, loading }: { router: NextRouter; price: Price; loading: boolean }) => {
	/* Translations */

	const localLang = {
		price: router.locale === "ar-AE" ? "السعر" : router.locale === "kn-IN" ? "ಬೆಲೆ" : "Price",
	};

	// if (!loading) {
	// 	return (
	// 		<ColumnContainer>
	// 			<Skeleton
	// 				animation="wave"
	// 				variant="text"
	// 				width="6rem"
	// 				height="0.75rem"
	// 				style={{ marginBottom: "0.5rem" }}
	// 			/>
	// 			<Skeleton
	// 				animation="wave"
	// 				variant="text"
	// 				width="5rem"
	// 				height="auto"
	// 				style={{ marginBottom: "0.5rem" }}
	// 			/>
	// 		</ColumnContainer>
	// 	);
	// }

	return (
		<ColumnContainer>
			{loading ? (
				<Skeleton
					animation="wave"
					variant="text"
					width="6rem"
					height="0.75rem"
				/>
			) : (
				<SubHeading>{localLang.price}</SubHeading>
			)}
			{loading ? (
				<Skeleton
					animation="wave"
					variant="text"
					width="5rem"
					height="1rem"
				/>
			) : (
				<Heading>
					{price?.currency === "AED" ? (
						<span style={{ fontFamily: "CurrencySymbols", marginRight: "0.25rem" }}>
							{String.fromCharCode(0xe001)}
						</span>
					) : (
						<RupeeIcon />
					)}
					{price !== undefined
						? price
							? (() => {
									const numberSystem = price.currency === "AED" ? "international" : "indian";
									return price.min === price.max
										? PreparePriceString(price.max, numberSystem)
										: PreparePriceString(price.min, numberSystem) + " - " + PreparePriceString(price.max, numberSystem);
							  })()
							: "Price not available"
						: ""}
				</Heading>
			)}
		</ColumnContainer>
	);
};

export default GridCardPrice;
